/**
 * LeadPops Brand Constants
 * All brand colors, gradients, shadows, and design tokens
 */

export const BRAND = {
  colors: {
    // Primary Brand Colors (from BRAND-GUIDELINES.md)
    primary: '#294bfd',           // Primary Blue
    primaryDark: '#1937e8',       // Primary Blue Dark (hover)
    primaryLight: '#4d6bff',      // Primary Blue Light
    featured: '#ff34a7',          // Accent Pink
    featuredDark: '#e91e8f',      // Accent Pink Dark (hover)
    featuredLight: '#ff5cb8',     // Accent Pink Light
    
    // Semantic Colors
    success: '#10b981',           // Success (emerald-500)
    warning: '#f59e0b',           // Warning (amber-500)
    error: '#ef4444',             // Error (red-500)
    info: '#06b6d4',              // Info (cyan-500)
    
    // Extended Palette
    purple: '#8b5cf6',
    indigo: '#6366f1',
    teal: '#14b8a6',
    orange: '#fb923c',
    yellow: '#fbbf24',
    
    // Neutral Gray Scale (following brand guidelines)
    gray: {
      950: '#0f172a',             // Darkest text, critical emphasis
      900: '#171717',             // Primary headings
      800: '#1e293b',             // Dark backgrounds
      700: '#374151',             // Body text
      600: '#475569',             // Secondary text
      500: '#64748b',             // Muted text
      400: '#94a3b8',             // Placeholder text
      300: '#cbd5e1',             // Borders
      200: '#e2e8f0',             // Dividers, light borders
      100: '#f1f5f9',             // Light backgrounds
      50: '#f8fafc'               // Lightest backgrounds
    },
    
    // Convenience aliases matching brand guidelines
    border: '#e2e8f0',            // gray-200
    background: '#ffffff',
    backgroundAlt: '#f8fafc',     // gray-50
    backgroundMuted: '#f1f5f9',   // gray-100
    accent: '#ff34a7',            // Same as featured
    neutral: '#64748b',           // gray-500
    
    text: {
      primary: '#374151',         // gray-700 (body text)
      secondary: '#64748b',       // gray-500 (muted text)
      muted: '#94a3b8',          // gray-400 (placeholder text)
      onDark: '#ffffff',
      accent: '#ff34a7'          // featured
    }
  },
  
  gradients: {
    // Hero Gradient (from brand guidelines)
    hero: 'linear-gradient(to bottom, rgb(243, 240, 255), rgb(255, 255, 255))',
    heroSection: 'linear-gradient(to bottom right, #eff6ff, #dbeafe)',
    
    // Text Gradient (Brand)
    textBrand: 'linear-gradient(135deg, #294bfd 0%, #ff34a7 100%)',
    
    // Button gradients
    primary: 'linear-gradient(135deg, #294bfd 0%, #1937e8 100%)',
    featured: 'linear-gradient(135deg, #ff34a7 0%, #e91e8f 100%)',
    
    // Icon container gradients (from brand guidelines)
    iconPurple: 'linear-gradient(to right, #8b5cf6, #7c3aed)',
    iconTeal: 'linear-gradient(to right, #06b6d4, #0891b2)',
    iconCoral: 'linear-gradient(to right, #f97316, #ea580c)',
    iconCyan: 'linear-gradient(to right, #0ea5e9, #0284c7)',
    iconPrimary: 'linear-gradient(to right, #294bfd, #2239e6)'
  },
  
  shadows: {
    // Elevation system (from brand guidelines)
    elevation1: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    elevation2: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    elevation3: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    elevation4: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    
    // Colored shadows for interactive states
    purple: '0 20px 25px -5px rgba(168, 85, 247, 0.25)',
    pink: '0 20px 25px -5px rgba(255, 52, 167, 0.25)',
    blue: '0 20px 25px -5px rgba(41, 75, 253, 0.25)'
  },
  
  radius: {
    // Standard radii (from brand guidelines)
    sm: '4px',                    // Badges, tags
    md: '6px',                    // Form inputs
    default: '8px',               // Buttons, cards
    lg: '12px',                   // Large cards
    xl: '16px'                    // Hero sections
  },
  
  fonts: {
    primary: 'var(--font-text), system-ui, sans-serif',
    weights: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700'
    }
  },
  
  // Animation durations (from brand guidelines)
  animation: {
    fast: '200ms',
    normal: '300ms',
    slow: '400ms',
    extraSlow: '600ms'
  },
  
  // Easing functions
  easing: {
    default: 'ease',
    bouncy: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',  // For CTAs
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)'              // For reveals
  }
} as const;